import React, { ReactNode, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'language/translator'

import { SxProps, useMediaQuery, useTheme } from '@mui/material'

import Box from 'components_new/atoms/Box'
import CardActionArea from 'components_new/atoms/Card/CardActionArea'
import Grid from 'components_new/atoms/Grid'
import Icon from 'components_new/atoms/Icon'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import GenericThumbnail from 'components_new/molecules/GenericThumbnail'
import LoadingSection from 'components_new/molecules/LoadingSection'

interface ThumbnailListProps {
  children: ReactNode[]
  columns?: number
  itemSize?: {
    xs?: number
    sm?: number
    md?: number
    lg?: number
    xl?: number
  }
  loading?: boolean
  onClickAdd?: () => void
  pinnedItems?: ReactNode[]
  spacing?: number
  sx?: SxProps
}

/**
 * The ThumbnailList component is used
 * to display several thumbnails on a
 * responsive way.
 */

const ThumbnailList = (props: ThumbnailListProps) => {
  const {
    children,
    columns,
    itemSize,
    loading,
    onClickAdd,
    pinnedItems,
    spacing = 2,
    sx
  } = props

  const theme = useTheme()
  const xl = useMediaQuery(theme.breakpoints.up('xl'), {
    noSsr: true
  })
  const { t } = useTranslation()

  const ref = useRef<HTMLElement>(null)

  const [scrollTop, setScrollTop] = useState(true)

  useEffect(() => {
    const element = ref.current
    const handleScroll = () => {
      if (element && element.scrollTop === 0) {
        setScrollTop(true)
      } else {
        setScrollTop(false)
      }
    }

    if (element) {
      element.addEventListener('scroll', handleScroll)

      // Clean-up
      return () => {
        element.removeEventListener('scroll', handleScroll)
      }
    }

    return
  }, [])

  const ItemSize = {
    xs: itemSize?.xs ?? 12,
    sm: itemSize?.sm ?? 6,
    md: itemSize?.md ?? 4,
    lg: itemSize?.lg ?? 3,
    xl: itemSize?.xl ?? 3
  }

  const ItemCols = columns ? columns : xl ? 15 : 12
  const hasPinnedItems = pinnedItems && pinnedItems.length > 0

  return (
    <>
      <Box
        ref={ref}
        sx={{
          width: '100%',
          overflowY: 'auto',
          borderTop: scrollTop ? undefined : '1px solid',
          borderTopColor: 'divider',
          pt: 2,
          ...sx
        }}
      >
        {loading ? (
          <LoadingSection sx={{ p: 2 }} />
        ) : children.length === 0 && !hasPinnedItems ? (
          <EmptyPlaceholder title={t('global', 'no_options_found')} />
        ) : (
          <>
            {/*-- pinned items --*/}
            {hasPinnedItems ? (
              <Grid
                container
                columns={ItemCols}
                spacing={spacing}
                sx={{ mb: 2 }}
              >
                {pinnedItems.map((thumbnail, i) => (
                  <Grid item {...ItemSize} key={i}>
                    {thumbnail}
                  </Grid>
                ))}
              </Grid>
            ) : null}

            <Grid container columns={ItemCols} spacing={spacing}>
              {children.map((thumbnail, i) => (
                <Grid item {...ItemSize} key={i}>
                  {thumbnail}
                </Grid>
              ))}

              {onClickAdd ? (
                <Grid item {...ItemSize}>
                  <Box sx={{ height: '100%', position: 'relative' }}>
                    {/*
                      Invisible real GenericThumbnail used as a sizer so
                      that the add card always gets exactly the same
                      intrinsic height as the regular thumbnails, even
                      when it is alone on a grid row (height '100%' only
                      stretches to siblings on the same row).
                    */}
                    <Box aria-hidden={true} sx={{ visibility: 'hidden' }}>
                      <GenericThumbnail
                        body={'\u00A0'}
                        disabled={true}
                        media={{ avatar: true }}
                        title={'\u00A0'}
                        variant="dashboard"
                      />
                    </Box>
                    <CardActionArea
                      onClick={onClickAdd}
                      sx={{
                        alignItems: 'center',
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        color: 'divider',
                        display: 'flex',
                        height: '100%',
                        inset: 0,
                        justifyContent: 'center',
                        position: 'absolute',
                        ['&:hover']: {
                          border: '1px solid',
                          borderColor: 'primary',
                          color: 'primary.main'
                        }
                      }}
                    >
                      <Icon color="inherit" name="Add" />
                    </CardActionArea>
                  </Box>
                </Grid>
              ) : null}
            </Grid>
          </>
        )}
      </Box>
    </>
  )
}

export default ThumbnailList
